import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router';
import { motion } from 'framer-motion';
import { Newspaper, ChevronLeft, ChevronRight, ExternalLink, Clock } from 'lucide-react';
import { getNews, getNewsDates } from '../../api/client';
import type { ApiResponse } from '../../types/api';

interface NewsItem {
  id: number;
  title: string;
  summary: string;
  description: string;
  source_name: string;
  source_url: string;
  image_url: string;
  category: string;
  news_date: string;
  published_at: string;
}

export default function NewsTickerSection(): React.JSX.Element {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [dates, setDates] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getNewsDates()
      .then((res: ApiResponse) => {
        if (res.dates && res.dates.length > 0) {
          setDates(res.dates);
          setSelectedDate(res.dates[0]);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedDate) return;
    setLoading(true);
    getNews({ date: selectedDate, page: 1, per_page: 8 })
      .then((res: ApiResponse) => {
        setNews(res.data || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [selectedDate]);

  const scroll = (dir: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const amount = dir === 'left' ? -320 : 320;
    scrollRef.current.scrollBy({ left: amount, behavior: 'smooth' });
  };

  const categoryColors: Record<string, string> = {
    'AI': 'text-blue-600 bg-blue-50',
    'AI Finance': 'text-emerald-600 bg-emerald-50',
    'AI Investments': 'text-violet-600 bg-violet-50',
    'AI Marketing': 'text-amber-600 bg-amber-50',
    'AI Layoffs': 'text-rose-600 bg-rose-50',
  };

  if (news.length === 0 && !loading) return <></>;

  return (
    <section className="py-12 px-4 bg-white border-y border-slate-100">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center">
              <Newspaper className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-black text-slate-900 tracking-tight">AI Daily</h2>
              <p className="text-xs text-slate-500 font-medium">
                {dates.length > 0 ? new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => scroll('left')}
              className="p-2 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
              aria-label="Scroll left"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => scroll('right')}
              className="p-2 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
              aria-label="Scroll right"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <Link
              to="/news"
              className="hidden sm:inline-flex ml-2 px-4 py-2 bg-slate-900 text-white text-xs font-bold uppercase tracking-wide rounded-lg hover:bg-slate-800 transition-colors"
            >
              View All
            </Link>
          </div>
        </div>

        {/* Date Chips */}
        {dates.length > 1 && (
          <div className="flex gap-2 mb-4 overflow-x-auto scrollbar-hide pb-1">
            {dates.map((d) => (
              <button
                key={d}
                onClick={() => setSelectedDate(d)}
                className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  selectedDate === d
                    ? 'bg-amber-500 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
              </button>
            ))}
          </div>
        )}

        {/* News Cards - Horizontal Scroll */}
        {loading ? (
          <div className="flex gap-4 overflow-hidden">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex-shrink-0 w-72 h-48 bg-slate-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : (
          <div
            ref={scrollRef}
            className="flex gap-4 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-2 -mx-4 px-4 sm:mx-0 sm:px-0"
            style={{ scrollSnapType: 'x mandatory' }}
          >
            {news.map((item, i) => (
              <motion.article
                key={item.id}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex-shrink-0 w-[280px] sm:w-[320px] snap-start group"
              >
                <Link to={`/news/${item.id}`} className="block bg-slate-50 rounded-xl overflow-hidden border border-slate-100 hover:shadow-lg hover:border-slate-200 transition-all h-full">
                  {/* Image */}
                  <div className="h-36 overflow-hidden bg-slate-200 relative">
                    {item.image_url ? (
                      <img
                        src={item.image_url}
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Newspaper className="w-8 h-8 text-slate-300" />
                      </div>
                    )}
                    <span className={`absolute top-2 left-2 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${categoryColors[item.category] || 'text-slate-600 bg-slate-100'}`}>
                      {item.category}
                    </span>
                  </div>
                  {/* Content */}
                  <div className="p-4">
                    <h3 className="text-sm font-bold text-slate-900 leading-snug mb-2 group-hover:text-amber-700 transition-colors line-clamp-2">
                      {item.title}
                    </h3>
                    <p className="text-xs text-slate-500 line-clamp-2 leading-relaxed mb-3">
                      {item.summary || item.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">{item.source_name}</span>
                      <a
                        href={item.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-[10px] font-bold text-amber-600 hover:text-amber-700 flex items-center gap-0.5"
                      >
                        Source <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                </Link>
              </motion.article>
            ))}

            {/* View All Card */}
            <div className="flex-shrink-0 w-[280px] sm:w-[320px] snap-start flex items-center justify-center">
              <Link
                to="/news"
                className="flex flex-col items-center justify-center w-full h-full min-h-[200px] bg-slate-50 rounded-xl border border-dashed border-slate-200 hover:border-amber-400 hover:bg-amber-50 transition-all group"
              >
                <Newspaper className="w-8 h-8 text-slate-300 group-hover:text-amber-500 mb-2 transition-colors" />
                <span className="text-sm font-bold text-slate-600 group-hover:text-amber-700">View All News</span>
                <span className="text-xs text-slate-400 mt-1">{news.length}+ stories</span>
              </Link>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
